import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { ActionRow, ProductRow, QuotaBase, QuotaBaseAction, QuotaRow, RequestInputRow, RequestRow } from "namirasoft-quota";
import { NSQAllUsageGauges } from "namirasoft-quota-react";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBarAlert, NSBoxEntity, NSBoxInteger, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { QuotaEntities } from "../QuotaEntities";
import { QuotaLayout } from "../QuotaLayout";
import { QuotaRouterProps } from "../QuotaRouterProps";

export interface RequestEditPageProps extends NSARouterMakerResult, QuotaRouterProps
{
    isEdit: boolean;
}

interface RequestEditPageState
{
    current_quota: QuotaRow | null;
    current_action: QuotaBaseAction | null;
    isSubscribed: boolean;
    loading: boolean;
}

export class RequestEditPage extends Component<RequestEditPageProps, RequestEditPageState>
{
    NSQAllUsageGauges_Ref = createRef<NSQAllUsageGauges>();

    NSBoxEntity_ProductID = createRef<NSBoxEntity<ProductRow>>();
    NSBoxEntity_ActionID = createRef<NSBoxEntity<ActionRow>>();
    NSBoxInteger_CurrentValue = createRef<NSBoxInteger>();
    NSBoxInteger_NewValue = createRef<NSBoxInteger>();

    private IsNSBoxProductIDInitialized: boolean = false;
    private IsNSBoxActionIDInitialized: boolean = false;

    constructor(props: RequestEditPageProps)
    {
        super(props);
        this.state = { current_quota: null, current_action: null, isSubscribed: false, loading: false };
        this.onActionIDChanged = this.onActionIDChanged.bind(this);
    }

    onActionIDChanged()
    {
        let product_id = this.NSBoxEntity_ProductID.current?.getValueOne(false) ?? "";
        let action_id = this.NSBoxEntity_ActionID.current?.getValueOne(false) ?? "";

        this.NSBoxInteger_CurrentValue.current?.setValue(null);
        this.setState((prev) => ({ ...prev, current_quota: null, current_action: null, loading: false }));

        if (!product_id || !action_id)
            return;

        this.setState((prev) => ({ ...prev, loading: true }));
        this.props.servers.quota.quota.GetBy(product_id, action_id).then(res =>
        {
            this.NSBoxInteger_CurrentValue.current?.setValue(res.value);
            this.setState((prev) => ({
                ...prev,
                current_quota: res,
                current_action: QuotaBase.getAction(product_id, action_id),
                loading: false
            }));
        }).catch(() => this.setState((prev) => ({ ...prev, loading: false })));
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        let product_id = this.NSBoxEntity_ProductID.current?.getValueOne(false) ?? "NONE";
        let action_id = this.NSBoxEntity_ActionID.current?.getValueOne(false) ?? "NONE";

        return (
            <QuotaLayout<RequestRow, RequestInputRow>
                {...this.props}
                action={{
                    description: this.props.isEdit ? "This section allows you to update the details of an existing quota change request." : "This section allows you to submit a request to increase or decrease your quota for tiered products.",
                }}
                pages={{
                    entity: QuotaEntities.request(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let product_id = this.NSBoxEntity_ProductID.current?.getValueOne() ?? "";
                                        let action_id_name = this.NSBoxEntity_ActionID.current?.getValueOne() ?? "";
                                        let new_value = this.NSBoxInteger_NewValue.current?.getValue() ?? 0;
                                        let new_unit = this.NSBoxInteger_NewValue.current?.NSBoxCombo_Ref.current?.getValueOne() ?? "";

                                        return {
                                            product_id,
                                            action_id_name,
                                            new_unit,
                                            new_value,
                                        };
                                    },
                                    setRow: (row) =>
                                    {
                                        this.NSBoxEntity_ProductID.current?.setValue(row.product_id);
                                        this.NSBoxEntity_ActionID.current?.setValue(row.action_id);
                                        this.NSBoxInteger_NewValue.current?.setValue(row.new_value);
                                        this.NSBoxInteger_NewValue.current?.NSBoxCombo_Ref.current?.setValueOne(row.new_unit);
                                    }
                                }
                            }
                        },
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxEntity<ProductRow>
                            ref={this.NSBoxEntity_ProductID}
                            title="Product"
                            input={{ name: "product" }}
                            required={true}
                            multiple={false}
                            table_name="product"
                            list={this.props.servers.quota.product.List}
                            getTitle={(row) => row.name}
                            getValue={(row) => row.id}
                            onChanged={() => this.NSBoxEntity_ActionID.current?.setValueOne(null, () => this.NSBoxEntity_ActionID.current?.reload())}
                            getSort={() => null}
                            onLoaded={() =>
                            {
                                if (!this.IsNSBoxProductIDInitialized)
                                {
                                    const product_id = this.props.url.getQuery("product_id");
                                    if (product_id)
                                        this.NSBoxEntity_ProductID.current?.setValueOne(product_id);
                                    this.IsNSBoxProductIDInitialized = true;
                                }
                            }}
                            info={{ link: "https://namirasoft.com/quota/console-guide/request/#field-product" }}

                        />
                        <NSBoxEntity<ActionRow>
                            ref={this.NSBoxEntity_ActionID}
                            title="Action"
                            input={{ name: "action" }}
                            table_name="action"
                            required={true}
                            defaultValue=""
                            multiple={false}
                            list={async (filters, page, size, sorts) =>
                            {
                                let product_id = this.NSBoxEntity_ProductID.current?.getValueOne(false) ?? "";

                                let ans: { count: number; rows: ActionRow[]; } = { count: 0, rows: [] };
                                if (product_id)
                                {
                                    if (!filters)
                                        filters = [];

                                    filters.push(new FilterItem(
                                        namirasoft.quota.tables.action,
                                        namirasoft.quota.tables.action.columns.product_id,
                                        false,
                                        FilterItemOperator.all.equals,
                                        product_id
                                    ));

                                    ans = await this.props.servers.quota.action.List(filters, page, size, sorts, null);
                                }
                                return ans;
                            }}
                            onChanged={this.onActionIDChanged}
                            getTitle={(row) => row.name}
                            getValue={(row) => row.id}
                            getSort={() => null}
                            onLoaded={() =>
                            {
                                if (this.IsNSBoxProductIDInitialized && !this.IsNSBoxActionIDInitialized)
                                {
                                    const action_id = this.props.url.getQuery("action_id");
                                    if (action_id)
                                        this.NSBoxEntity_ActionID.current?.setValueOne(action_id);
                                    this.IsNSBoxActionIDInitialized = true;
                                }
                            }}
                            info={{ link: "https://namirasoft.com/quota/console-guide/request/#field-action" }}
                        />
                    </NSRow>
                    <NSRow style={{ justifyContent: "flex-start" }}>
                        <NSBoxInteger
                            ref={this.NSBoxInteger_CurrentValue}
                            title={`Current Value ${this.state.current_quota?.unit ? `(${this.state.current_quota.unit})` : ""}`}
                            required={true}
                            disabled
                            loading={this.state.loading}
                            combo={this.state.current_quota?.unit
                                ?
                                {
                                    multiple: false,
                                    getOptions: () => this.state.current_action?.units.map(u => ({ value: u, title: u })) ?? [],
                                    defaultValue: this.state.current_quota.unit,
                                    disabled: true
                                }
                                : undefined
                            }
                            info={{ link: "https://namirasoft.com/quota/console-guide/request/#field-current-value" }}
                        />
                        <NSBoxInteger
                            key={this.state.current_action?.id}
                            ref={this.NSBoxInteger_NewValue}
                            title="New Value"
                            required={true}
                            input={{ name: "new-value" }}
                            combo={this.state.current_quota?.unit
                                ?
                                {
                                    multiple: false,
                                    getOptions: () => this.state.current_action?.units.map(u => ({ value: u, title: u })) ?? []
                                }
                                : undefined
                            }
                            info={{ link: "https://namirasoft.com/quota/console-guide/request/#field-new-value" }}
                        />
                    </NSRow>

                    {
                        this.state.current_quota &&
                        <NSColumn style={{ padding: "1rem 0", alignItems: "center" }}>
                            {
                                !this.NSQAllUsageGauges_Ref.current?.isSubscribed() &&
                                <NSBarAlert
                                    title={{ title: "", description: "Quota limits will apply only after you subscribe to the specific service." }}
                                    button_text=""
                                    isVisible={() => true}
                                />
                            }
                            <NSQAllUsageGauges
                                {...this.props}
                                ref={this.NSQAllUsageGauges_Ref}
                                filter={{
                                    product_id,
                                    action_ids: [action_id]
                                }}
                                onLoaded={() =>
                                {
                                    this.setState((prev) => ({ ...prev, isSubscribed: this.NSQAllUsageGauges_Ref.current?.isSubscribed() ?? false }));
                                }}
                            />
                        </NSColumn>
                    }

                </NSColumn>
            </QuotaLayout >
        );
    }
}